import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import CustomDialog from '../custom-material/dialog/Dialog'
import CustomDialogTitle from '../custom-material/dialog/DialogTitle'
import { CenteredCustomTypography } from '../custom-material/typography/Typography'
import CustomButton, { CancelCustomButton } from '../custom-material/button/Button'
import { isEmpty, flatten } from 'lodash'

const styles = theme => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px'
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    width: '50%'
  }
})

/**
 * Render Dialog Content block for a single action type happening (activation or deactivation)
 * @param {boolean} [type] to configure text difference between activation/deactivation
 * @param {object[]} [itemsToUpdate] array of items (either activations or deactivations) that will be updated if user confirms
 */
const SingleActionContent = ({type, itemsToUpdate}) => (
  <React.Fragment>
    <CenteredCustomTypography variant='body1' paragraph>
      Please confirm you would like to request the <strong>{type}</strong> of the following product(s) on your IPD Analytics subscription:
    </CenteredCustomTypography>
    {itemsToUpdate.map((x, i) => <CenteredCustomTypography key={i} variant='body1'>{x.productGroupName} {x.productName} ({x.name})</CenteredCustomTypography>)}<br />
    <CenteredCustomTypography variant='body1' paragraph>Do you want to continue?</CenteredCustomTypography>
  </React.Fragment>
)

/**
 * Render Dialog Content block for a multi action type happening (activations and deactivations)
 * @param {object[]} [activations] array of activations that will be updated if user confirms
 * @param {object[]} [deactivations] array of deactivations that will be updated if user confirms
 */
const MultiActionContent = ({activations, deactivations}) => (
  <React.Fragment>
    <CenteredCustomTypography variant='body1' paragraph>
      Please confirm you would like to request the <strong>deactivation</strong> of the following product(s):
    </CenteredCustomTypography>
    {deactivations.map((x, i) => <CenteredCustomTypography key={i} variant='body1'>{x.productGroupName} {x.productName} ({x.name})</CenteredCustomTypography>)}<br />
    <CenteredCustomTypography variant='body1' paragraph>
      and the <strong>activation</strong> of the following product(s):
    </CenteredCustomTypography>
    {activations.map((x, i) => <CenteredCustomTypography key={i} variant='body1'>{x.productGroupName} {x.productName} ({x.name})</CenteredCustomTypography>)}<br />
    <CenteredCustomTypography variant='body1' paragraph>for your IPD Analytics subscription</CenteredCustomTypography><br />
    <CenteredCustomTypography variant='body1' paragraph>Do you want to continue?</CenteredCustomTypography><br />
  </React.Fragment>
)

class SubscriptionUpdateDialogComponent extends React.PureComponent {
  render () {
    const { classes, dialogOpen, updates, handleCloseDialog, saveSubscription } = this.props
    const products = flatten(
      updates.products
        .map(product => product.regions
          .map(region => ({
            ...region,
            productGroupName: product.productGroupName,
            productName: product.name
          }))
        )
    )
    const activations = products.filter(product => product.operationType === 'Add')
    const deactivations = products.filter(product => product.operationType === 'Remove')
    const hasActivations = !isEmpty(activations)
    const hasDeactivations = !isEmpty(deactivations)
    const itemsToUpdate = hasActivations ? activations : deactivations // to use when either activations or deactivations are the only ones to display
    const hasBoth = hasActivations && hasDeactivations
    const hasNone = !hasActivations && !hasDeactivations
    return (
      <CustomDialog open={dialogOpen} onClose={handleCloseDialog} aria-labelledby='updates-dialog-title'>
        <CustomDialogTitle id='updates-dialog-title' disableTypography>Request to Make Product Access Changes</CustomDialogTitle>
        <div className={classes.contentContainer}>
          {hasNone
            ? <React.Fragment /> // @TODO - empty state
            : hasBoth
              ? <MultiActionContent activations={activations} deactivations={deactivations} />
              : <SingleActionContent type={hasActivations ? 'activation' : 'deactivation'} itemsToUpdate={itemsToUpdate} />
          }
          <div className={classes.actionContainer}>
            <CancelCustomButton variant='contained' size='small' color='default'  onClick={handleCloseDialog}>Cancel</CancelCustomButton>
            <CustomButton variant='contained' size='small' color='secondary'  onClick={() => { saveSubscription(); handleCloseDialog() }}>Yes</CustomButton>
          </div>
        </div>
      </CustomDialog>
    )
  }
}

SubscriptionUpdateDialogComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  dialogOpen: PropTypes.bool,
  updates: PropTypes.object,
  handleCloseDialog: PropTypes.func,
  saveSubscription: PropTypes.func
}

export default withStyles(styles)(SubscriptionUpdateDialogComponent)
